import { FC, useMemo, useState } from 'react';
import { RobotOutlined } from '@ant-design/icons';
import { Collapse, CollapseProps } from 'antd';
import styled from 'styled-components';
import { themeConfig, useThemeContext } from '@/shared/theme';
import { ChatMode } from '@/shared/types';
import { Col, Row } from '@/shared/ui';
import { useOrgAiConversation } from './lib/useOrgAiConversation';
import { ConversationMode } from './model/types';
import ChatConversationSwitch from './ui/ChatConversationSwitch';
import { ChatHistory } from './ui/ChatHistory';
import ChatInput from './ui/ChatInput';

type Props = {
  orgId: string | undefined;
  chatMode?: ChatMode;
  label?: string;
  isOpenByDefault?: boolean;
  isCollapsible?: boolean;
};

export type ChatRefProps = {
  openChat: () => void;
  closeChat: () => void;
};

export const OrganizationChat: FC<Props> = ({
  orgId,
  label = 'Live chat',
  chatMode,
  isOpenByDefault = false,
  isCollapsible = true,
}) => {
  const { theme } = useThemeContext();
  const [isLoading, setIsLoading] = useState(false);
  const [isOpenAIKeyDeleted, setIsOpenAIKeyDeleted] = useState(false);
  const [conversationMode, setConversationMode] = useState<ConversationMode>(
    ConversationMode.AI,
  );

  const {
    aiConversationMessages,
    aiConversationLoading,
    startConversationLoading,
    waitingToStartGenerating,
    streamedMessage,
    sendMessageToAi,
    isMyMessageInAiConversation,
  } = useOrgAiConversation({
    orgId,
    chatMode,
    setIsOpenAIKeyDeleted,
    setIsLoading,
  });

  const conversationOptions = useMemo(() => {
    return [
      {
        label: <span data-testid="ai-label">AI</span>,
        value: ConversationMode.AI,
        icon: <RobotOutlined />,
      },
    ];
  }, []);

  const isInputDisabled = useMemo(
    () =>
      isLoading ||
      chatMode === ChatMode.Preview ||
      (chatMode === ChatMode.User && isOpenAIKeyDeleted),
    [isLoading, chatMode, isOpenAIKeyDeleted],
  );

  const collapseItems: CollapseProps['items'] = [
    {
      key: `org-chat`,
      label: <span>{label}</span>,
      collapsible: isCollapsible ? 'header' : 'icon',
      showArrow: isCollapsible,
      children: (
        <div>
          <ContentWrapper>
            {conversationMode === ConversationMode.AI && (
              <ChatHistory
                messages={aiConversationMessages}
                chatLoading={startConversationLoading || aiConversationLoading}
                messageLoading={waitingToStartGenerating}
                isMyMessage={isMyMessageInAiConversation}
                chatMode={chatMode}
                streamedMessage={streamedMessage}
              />
            )}
          </ContentWrapper>
          <MessageInputWrapper
            style={{
              borderTop: `1px solid ${themeConfig[theme].colors.border}`,
            }}
          >
            <Col alignItems="flex-start" style={{ width: '100%' }}>
              <Row
                alignItems="center"
                style={{ marginBottom: 10, position: 'relative' }}
              >
                <ChatConversationSwitch
                  chatMode={chatMode}
                  conversationOptions={conversationOptions}
                  conversationMode={conversationMode}
                  onChatConversationChange={setConversationMode}
                />
              </Row>
              <Row gap={5} direction="column">
                <ChatInput
                  disabled={isInputDisabled}
                  placeholder={'Ask AI'}
                  isLoading={isLoading}
                  sendMessage={sendMessageToAi}
                />
              </Row>
            </Col>
          </MessageInputWrapper>
          {conversationMode === ConversationMode.AI && (
            <Info>
              AI Assistant can make mistakes. Consider checking important
              information.
            </Info>
          )}
        </div>
      ),
    },
  ];

  return (
    <Collapse
      defaultActiveKey={isOpenByDefault ? 'org-chat' : undefined}
      data-testid="chat-collapse"
      size="small"
      style={{ width: '100%' }}
      items={collapseItems}
    />
  );
};

const ContentWrapper = styled.div`
  height: 50vh;
`;

const MessageInputWrapper = styled.div`
  border-bottom-left-radius: 6px;
  border-bottom-right-radius: 6px;
  border-top: 1px solid #d9d9d9;
  position: relative;
  display: flex;
  padding: 16px 12px;
  margin-bottom: 10px;

  .ant-input-data-count {
    font-size: 11px;
  }
`;

const Info = styled.div`
  opacity: 0.6;
  font-size: 11px;
  text-align: center;
  margin-top: -5px;
  margin-bottom: 5px;
`;
